// Country colours, as a texture the fragment shader looks up.
//
// The board texture carries a country ID, not a colour. That is the difference
// between a 320x320 upload of two bytes a pixel and one of four, and — more
// importantly — it means recolouring a country, or swapping the whole palette for
// real flag colours when the map data lands, is a 256x256 texture upload rather
// than a full board re-pack.
//
// SPEC §15: country flag colours sit at FULL SATURATION because they are the
// content, and everything else in the interface is deliberately drained so they
// carry. Neutral is the exception — it is territory nobody scores, and it takes
// the interface's own drained line colour so it reads as background.
//
// The generated palette is a placeholder with a real property, not a stand-in
// with none: hues are spaced by the golden angle, so consecutive country ids —
// which the seeder hands to spatially ADJACENT blobs — never land near each other
// on the colour wheel. Two neighbouring countries in the same green is the one
// failure mode a random palette produces constantly.
//
// ACCESSIBILITY (§15, "not negotiable"): country identity must never rest on
// colour alone. This module is the colour half; the renderer's hover reticle
// names the owner in text, and the flag-pattern half belongs with the real map
// data. Nothing here should grow into the only cue.

import { NeutralCountry } from '../wire/core';
import tokens from '../tokens.json';

/** The palette texture is 256x256 RGBA8, indexed by country id: x = id & 255, y = id >> 8. */
export const PaletteWidth = 256;
export const PaletteHeight = 256;
export const PaletteEntries = PaletteWidth * PaletteHeight;

/** The golden angle in turns. Consecutive ids land as far apart on the wheel as possible. */
const GOLDEN_ANGLE = 0.618033988749895;

export interface RGB {
  r: number;
  g: number;
  b: number;
}

export function hexToRGB(hex: string): RGB {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.replace(/./g, (c) => c + c) : h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/**
 * The generated colour for a country id.
 *
 * Saturation and lightness ride two short cycles that are coprime with nothing in
 * particular — they just have to be short and different, so that ids far apart in
 * hue are also separated in tone for anyone who reads hue poorly.
 */
export function generatedColour(id: number): RGB {
  if (id === NeutralCountry) return hexToRGB(tokens.color.line);
  const h = (id * GOLDEN_ANGLE) % 1;
  const s = 0.62 + 0.28 * ((id % 3) / 2);
  const l = 0.44 + 0.22 * ((id % 5) / 4);
  return hslToRGB(h, s, l);
}

export function hslToRGB(h: number, s: number, l: number): RGB {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = ((h % 1) + 1) % 1 * 6;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r = 0;
  let g = 0;
  let b = 0;
  if (hp < 1) [r, g, b] = [c, x, 0];
  else if (hp < 2) [r, g, b] = [x, c, 0];
  else if (hp < 3) [r, g, b] = [0, c, x];
  else if (hp < 4) [r, g, b] = [0, x, c];
  else if (hp < 5) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const m = l - c / 2;
  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
  };
}

/**
 * The palette as texture bytes.
 *
 * Explicit overrides win, so the day the real map data lands its flag colours are
 * a `set` call each and nothing else in the renderer changes.
 */
export class Palette {
  readonly texels = new Uint8Array(PaletteEntries * 4);
  private readonly overrides = new Map<number, RGB>();
  /** Bumped on every change, so the renderer can re-upload only when it must. */
  private rev = 0;

  constructor() {
    this.rebuild();
  }

  revision(): number {
    return this.rev;
  }

  /** Pins one country to a real colour. */
  set(id: number, colour: RGB | string): void {
    this.overrides.set(id, typeof colour === 'string' ? hexToRGB(colour) : colour);
    this.write(id);
    this.rev++;
  }

  /** Pins many at once, for a flag table loaded in one go. */
  setAll(table: Record<number, RGB | string>): void {
    for (const [k, v] of Object.entries(table)) {
      const id = Number(k);
      this.overrides.set(id, typeof v === 'string' ? hexToRGB(v) : v);
      this.write(id);
    }
    this.rev++;
  }

  colour(id: number): RGB {
    return this.overrides.get(id) ?? generatedColour(id);
  }

  private write(id: number): void {
    const c = this.colour(id);
    const at = id * 4;
    this.texels[at] = c.r;
    this.texels[at + 1] = c.g;
    this.texels[at + 2] = c.b;
    // Alpha is not opacity here: it is the "this id is real territory" bit, so a
    // country the palette has never heard of is still drawn rather than made
    // invisible. Neutral is opaque too — it is territory, just nobody's.
    this.texels[at + 3] = 255;
  }

  private rebuild(): void {
    for (let id = 0; id < PaletteEntries; id++) this.write(id);
    this.rev++;
  }
}
